// Overview — answers "are we on track this month?" first, then where the gaps are.
import { Fragment, useEffect, useMemo, useState } from "react";
import {
  ArrowUpDown, TrendingDown, TrendingUp, AlertTriangle, CalendarClock, CheckCircle, Image, Wand2, Clapperboard,
  Sparkles, Video, FileText, ChevronRight, Building2, Target, Flame, Layers, Plus, Users as Users2,
} from "lucide-react";
import { mpApi, SERVICE_KEYS, serviceMeta, chipProps, ProgressBar, pctOf, clampPct, monthLabel, initials, isOverdue, bucketOf, fmtDay, istToday } from "./mpShared";
import { Link } from "react-router-dom";
import ClientPanel from "./ClientPanel";

const SORTS = [
  ["target", "Largest target"], ["pct_asc", "Lowest completion"], ["pct_desc", "Highest completion"],
  ["behind", "Most behind pace"], ["overdue", "Most overdue"], ["name", "Client name A–Z"],
];
const SERVICE_ICONS = { creatives: Image, motion_graphic: Wand2, animated_video: Clapperboard, ai_video: Sparkles, reel_shoot: Video, blog: FileText };
const addDays = (iso, n) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + n); return d.toLocaleDateString("en-CA"); };

/** Completion ring (plain SVG so it stays crisp and themeable). */
function Ring({ pct, size = 132, stroke = 12, color = "#34d399", track = "rgba(255,255,255,0.12)", children }) {
  const r = (size - stroke) / 2, c = 2 * Math.PI * r;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke={track} strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke} fill="none" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - clampPct(pct) / 100)} style={{ transition: "stroke-dashoffset .6s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">{children}</div>
    </div>
  );
}

export default function OverviewTab({ overview, filters, month, members, clients, canManage, refresh, openTask, newTask, businessId, onManageServices }) {
  const [sort, setSort] = useState("target");
  const [panel, setPanel] = useState(null); // { client, service }
  const [atRiskOnly, setAtRiskOnly] = useState(false);
  const [tasks, setTasks] = useState(null);
  const [showOthers, setShowOthers] = useState(false);
  const t = overview.totals;
  const st = overview.service_totals;
  const today = overview.today || istToday();
  const memberName = useMemo(() => Object.fromEntries(members.map((m) => [m.user_id, m.name])), [members]);
  const clientName = useMemo(() => Object.fromEntries(overview.clients.map((c) => [c.client_id, c.name])), [overview]);

  // Team workload (top of the list) for the landing page.
  const [team, setTeam] = useState(null);
  useEffect(() => {
    mpApi("/workload", { params: { month, business_id: businessId || undefined } }).then((d) => setTeam(d)).catch(() => setTeam({ members: [], unassigned: { total: 0 } }));
  }, [month, businessId, overview]);

  // Tasks feed the "Due this week" and "Needs attention" panels.
  useEffect(() => {
    mpApi("/tasks", { params: { month, business_id: businessId || undefined } }).then((d) => setTasks(d.tasks)).catch(() => setTasks([]));
  }, [month, businessId, overview]);

  // How far through the month we are.
  const elapsed = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const days = new Date(y, m, 0).getDate();
    if (today.slice(0, 7) > month) return 100;
    if (today.slice(0, 7) < month) return 0;
    return Math.round((Number(today.slice(8, 10)) / days) * 100);
  }, [month, today]);
  // Progress counts at most the target per service (server-side "achieved"); surplus is "extra".
  const achieved = t.achieved ?? t.completed;
  const expectedByNow = Math.round((t.target * elapsed) / 100);
  const gap = expectedByNow - achieved; // >0 = behind pace
  const inFlight = Math.min(t.in_progress, Math.max(0, t.target - achieved));
  const notStarted = Math.max(0, t.target - achieved - inFlight);

  const rows = useMemo(() => {
    const s = (filters.search || "").toLowerCase();
    let list = overview.clients.filter((c) => (!filters.client_id || c.client_id === filters.client_id) && (!s || c.name.toLowerCase().includes(s)));
    if (filters.service && filters.service !== "none") list = list.filter((c) => (c.by_service[filters.service] || {}).target > 0 || (c.by_service[filters.service] || {}).total > 0);
    if (filters.overdue) list = list.filter((c) => c.totals.overdue > 0);
    list = list.map((c) => {
      const behind = Math.max(0, Math.round((c.target_total * elapsed) / 100) - (c.totals.achieved ?? c.totals.completed));
      return { ...c, behind, at_risk: c.target_total > 0 && (c.totals.overdue > 0 || c.totals.pct + 20 < elapsed) };
    });
    if (atRiskOnly) list = list.filter((c) => c.at_risk);
    const cmp = {
      target: (a, b) => b.target_total - a.target_total || a.name.localeCompare(b.name),
      pct_asc: (a, b) => a.totals.pct - b.totals.pct,
      pct_desc: (a, b) => b.totals.pct - a.totals.pct,
      behind: (a, b) => b.behind - a.behind,
      overdue: (a, b) => b.totals.overdue - a.totals.overdue,
      name: (a, b) => a.name.localeCompare(b.name),
    }[sort];
    return list.sort(cmp);
  }, [overview, filters, sort, atRiskOnly, elapsed]);
  const planned = rows.filter((c) => c.target_total > 0);
  const others = rows.filter((c) => c.target_total === 0);
  const tableRows = showOthers || planned.length === 0 ? [...planned, ...others] : planned;
  const atRisk = useMemo(() => rows.filter((c) => c.at_risk).sort((a, b) => b.totals.overdue - a.totals.overdue || b.behind - a.behind), [rows]);

  // "Upcoming" follows the month on screen: this week (current month), the first week
  // (future month) or everything still open (past month).
  const viewing = today.slice(0, 7) === month ? "current" : month > today.slice(0, 7) ? "future" : "past";
  const winStart = viewing === "current" ? today : `${month}-01`;
  const weekEnd = addDays(winStart, 6);
  const upcomingTitle = viewing === "current" ? "Due this week" : viewing === "future" ? "First week of the month" : "Still open from this month";
  const dueThisWeek = useMemo(() => (tasks || [])
    .filter((x) => {
      const d = String(x.due_date || "").slice(0, 10); const open = bucketOf(x.status) !== "completed" && bucketOf(x.status) !== "cancelled";
      return open && (viewing === "past" ? true : d >= winStart && d <= weekEnd);
    })
    .sort((a, b) => String(a.due_date).localeCompare(String(b.due_date))), [tasks, winStart, weekEnd, viewing]);
  const overdueTasks = useMemo(() => (tasks || []).filter((x) => isOverdue(x, today)).sort((a, b) => String(a.due_date).localeCompare(String(b.due_date))), [tasks, today]);

  const segments = [
    ["Done", achieved, "bg-emerald-400"],
    ["In progress", inFlight, "bg-sky-400"],
    ["Not started", notStarted, "bg-white/25"],
  ];
  const paceTone = t.target === 0 ? "text-slate-300" : gap <= 0 ? "text-emerald-300" : gap <= t.target * 0.1 ? "text-amber-300" : "text-rose-300";

  return (
    <div className="space-y-4">
      {/* ── Hero: are we on track? ── */}
      <div className="overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950 p-5 text-white shadow-lg md:p-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center">
          <div className="flex items-center gap-5">
            <Ring pct={t.pct}>
              <span className="text-3xl font-semibold">{t.pct}%</span>
              <span className="text-[11px] text-slate-300">complete</span>
            </Ring>
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-slate-400">{monthLabel(month)}</div>
              <div className="mt-1 text-2xl font-semibold">{achieved} <span className="text-slate-400">of</span> {t.target}</div>
              <div className="text-sm text-slate-300">target deliverables done across {t.planned_clients ?? t.clients} client{(t.planned_clients ?? t.clients) === 1 ? "" : "s"}</div>
              {t.extra > 0 && <div className="mt-0.5 text-xs text-slate-400">+{t.extra} extra completed beyond targets</div>}
              <div className={`mt-2 inline-flex items-center gap-1.5 text-sm font-medium ${paceTone}`}>
                {t.target === 0 ? "No targets set for this month"
                  : gap <= 0 ? <><TrendingUp className="h-4 w-4" /> On track — {Math.abs(gap)} ahead of pace</>
                  : <><TrendingDown className="h-4 w-4" /> {gap} behind pace — should be ~{expectedByNow} done by today</>}
              </div>
            </div>
          </div>

          <div className="min-w-0 flex-1 lg:border-l lg:border-white/10 lg:pl-6">
            <div className="flex items-center justify-between text-xs text-slate-400">
              <span>Status of all {t.target} target deliverables</span>
              <span>{elapsed}% of the month elapsed</span>
            </div>
            <div className="relative mt-2 flex h-3 overflow-hidden rounded-full bg-white/10">
              {t.target > 0 && segments.map(([label, n, cls]) => n > 0 && <div key={label} className={cls} style={{ width: `${(n / t.target) * 100}%` }} title={`${label}: ${n}`} />)}
              {t.target > 0 && elapsed > 0 && elapsed < 100 && <div className="absolute inset-y-0 w-0.5 bg-white" style={{ left: `${elapsed}%` }} title="Today" />}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                ["Done (of target)", achieved, "text-emerald-300", CheckCircle],
                ["In progress", inFlight, "text-sky-300", Target],
                ["Not started", notStarted, "text-slate-200", Building2],
                ["Overdue", t.overdue, t.overdue ? "text-rose-300" : "text-slate-200", Flame],
              ].map(([label, n, cls, Icon]) => (
                <div key={label} className="rounded-2xl bg-white/5 px-3 py-2.5 ring-1 ring-white/10">
                  <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-400"><Icon className="h-3.5 w-3.5" />{label}</div>
                  <div className={`mt-0.5 text-2xl font-semibold ${cls}`}>{n}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Service tiles ── */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {SERVICE_KEYS.map((k) => {
          const s = st[k]; if (!s) return null; const m = serviceMeta(k); const Icon = SERVICE_ICONS[k] || Layers;
          const done = s.achieved ?? Math.min(s.completed, s.target);
          const p = s.target ? pctOf(done, s.target) : 0;
          return (
            <div key={k} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ backgroundColor: `${m.color}1a`, color: m.color }}><Icon className="h-[18px] w-[18px]" /></span>
                <span className="text-xs font-semibold text-slate-500">{p}%</span>
              </div>
              <div className="mt-3 text-sm font-medium text-slate-600">{m.label}</div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-semibold text-slate-900">{done}</span>
                <span className="text-sm text-slate-400">/ {s.target}</span>
                {s.extra > 0 && <span className="ml-auto rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700" title="Completed beyond the target">+{s.extra}</span>}
              </div>
              <div className="mt-2"><ProgressBar pct={p} color={m.color} /></div>
              <div className="mt-1.5 text-[11px] text-slate-500">
                {s.target === 0 ? "No target" : s.remaining === 0 ? <span className="font-medium text-emerald-600">Target met</span> : `${s.remaining ?? Math.max(0, s.target - done)} left`}{s.overdue ? <span className="font-medium text-rose-600"> · {s.overdue} overdue</span> : ""}
              </div>
            </div>
          );
        })}
        {canManage && onManageServices && (
          <button onClick={onManageServices} data-testid="mp-add-service-tile"
            className="flex min-h-[150px] flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-200 bg-white/50 p-4 text-sm font-medium text-slate-500 transition hover:border-indigo-300 hover:bg-indigo-50/40 hover:text-indigo-700">
            <Plus className="h-5 w-5" /> Add service
          </button>
        )}
      </div>

      {/* ── Client table ── */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Clients this month</h2>
            <p className="text-xs text-slate-500">Completed / target per service · click a client or a cell for its tasks</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setAtRiskOnly((v) => !v)} data-testid="mp-at-risk"
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold ring-1 ring-inset transition ${atRiskOnly ? "bg-rose-600 text-white ring-rose-600" : "bg-white text-rose-700 ring-rose-200 hover:bg-rose-50"}`}>
              <AlertTriangle className="h-3.5 w-3.5" /> At risk · {overview.clients.filter((c) => c.target_total > 0 && (c.totals.overdue > 0 || c.totals.pct + 20 < elapsed)).length}
            </button>
            <label className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-500">
              <ArrowUpDown className="h-3.5 w-3.5" />
              <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-transparent py-1.5 text-xs text-slate-700 outline-none">
                {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="py-14 text-center text-sm text-slate-500">
            {overview.clients.length === 0 ? "No clients have targets or tasks this month. Add targets in Service Targets." : "No clients match these filters."}
          </div>
        ) : (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full min-w-[980px] text-sm">
                <thead className="text-left text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  <tr className="border-b border-slate-100">
                    <th className="w-12 py-3 pl-5 pr-1">#</th>
                    <th className="px-3 py-3">Client</th>
                    {SERVICE_KEYS.map((k) => (
                      <th key={k} className="px-1.5 py-3 text-center">
                        <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: serviceMeta(k).color }} />{serviceMeta(k).short}</span>
                      </th>
                    ))}
                    <th className="px-3 py-3 text-right">Done</th>
                    <th className="w-44 px-5 py-3">Progress</th>
                    <th className="w-6" />
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((c, idx) => (
                    <Fragment key={c.client_id}>
                    {idx === planned.length && others.length > 0 && (
                      <tr key="others-head"><td colSpan={SERVICE_KEYS.length + 5} className="bg-slate-50 px-5 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Other clients with work this month — no targets set</td></tr>
                    )}
                    <tr key={c.client_id} onClick={() => setPanel({ client: c, service: "" })} data-testid={`mp-client-${c.client_id}`}
                      className="group cursor-pointer border-b border-slate-50 transition hover:bg-slate-50/80">
                      <td className="py-3 pl-5 pr-1 text-sm font-medium tabular-nums text-slate-400">{idx + 1}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-50 to-slate-100 text-xs font-bold text-indigo-700">{initials(c.name)}</span>
                          <div className="min-w-0">
                            <div className="truncate font-medium text-slate-900">{c.name}</div>
                            <div className="flex items-center gap-1.5 text-[11px]">
                              {c.target_total === 0 ? <span className="text-slate-400">No targets</span>
                                : c.totals.overdue > 0 ? <span className="font-medium text-rose-600">{c.totals.overdue} overdue</span>
                                : c.behind > 0 && c.at_risk ? <span className="font-medium text-amber-600">{c.behind} behind pace</span>
                                : <span className="font-medium text-emerald-600">On track</span>}
                              {c.other.total > 0 && <span className="text-slate-400">· {c.other.total} untagged</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      {SERVICE_KEYS.map((k) => {
                        const s = c.by_service[k];
                        if (!s || (s.target === 0 && s.total === 0)) return <td key={k} className="px-1.5 py-3 text-center text-slate-200">—</td>;
                        const p = s.target ? clampPct(pctOf(s.completed, s.target)) : 0;
                        const full = s.target > 0 && s.completed >= s.target;
                        return (
                          <td key={k} className="px-1.5 py-3 text-center">
                            <button onClick={(e) => { e.stopPropagation(); setPanel({ client: c, service: k }); }}
                              title={`${serviceMeta(k).label}: ${s.completed} of ${s.target} done · ${s.total} tasks created${s.overdue ? ` · ${s.overdue} overdue` : ""}`}
                              className={`mx-auto block w-[58px] rounded-lg px-1.5 py-1 transition hover:bg-white hover:shadow-sm hover:ring-1 hover:ring-slate-200 ${s.overdue ? "bg-rose-50" : full ? "bg-emerald-50" : ""}`}>
                              <div className="text-xs font-semibold tabular-nums text-slate-800">{s.completed}<span className="font-normal text-slate-400">/{s.target}</span></div>
                              <div className="mt-1 h-1 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full" style={{ width: `${p}%`, backgroundColor: serviceMeta(k).color }} /></div>
                            </button>
                          </td>
                        );
                      })}
                      <td className="px-3 py-3 text-right text-sm tabular-nums">
                        {c.target_total > 0
                          ? <><span className="font-semibold text-slate-900">{c.totals.achieved ?? c.totals.completed}</span><span className="text-slate-400">/{c.target_total}</span>{c.totals.extra > 0 && <div className="text-[10px] font-medium text-emerald-600">+{c.totals.extra} extra</div>}</>
                          : <span className="text-slate-500">{c.totals.completed} done</span>}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <ProgressBar pct={c.totals.pct} color={c.totals.pct >= 100 ? "#10b981" : c.at_risk ? "#f43f5e" : "#6366f1"} />
                          <span className="w-9 text-right text-xs font-semibold tabular-nums text-slate-700">{c.totals.pct}%</span>
                        </div>
                      </td>
                      <td className="pr-4"><ChevronRight className="h-4 w-4 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-slate-500" /></td>
                    </tr>
                    </Fragment>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50/80 text-xs font-semibold text-slate-600">
                  <tr>
                    <td className="py-3 pl-5 pr-1" />
                    <td className="px-3 py-3">Against targets</td>
                    {SERVICE_KEYS.map((k) => <td key={k} className="px-1.5 py-3 text-center tabular-nums">{st[k] ? `${st[k].achieved ?? st[k].completed}/${st[k].target}` : "—"}</td>)}
                    <td className="px-3 py-3 text-right tabular-nums">{achieved}/{t.target}</td>
                    <td className="px-5 py-3">{t.pct}%</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
              {others.length > 0 && planned.length > 0 && (
                <button onClick={() => setShowOthers((v) => !v)} className="w-full border-t border-slate-100 px-5 py-2.5 text-left text-xs font-medium text-indigo-600 hover:bg-slate-50">
                  {showOthers ? "Hide" : "Show"} {others.length} other client{others.length === 1 ? "" : "s"} with work but no targets
                </button>
              )}
            </div>

            <div className="space-y-2 p-3 md:hidden">
              {tableRows.map((c, idx) => (
                <button key={c.client_id} onClick={() => setPanel({ client: c, service: "" })} className="w-full rounded-2xl border border-slate-200 p-3 text-left">
                  <div className="flex items-center gap-3">
                    <span className="w-5 text-right text-xs font-medium tabular-nums text-slate-400">{idx + 1}</span>
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-xs font-bold text-indigo-700">{initials(c.name)}</span>
                    <span className="flex-1 font-medium text-slate-900">{c.name}</span>
                    <span className="text-sm font-semibold text-slate-700">{c.totals.pct}%</span>
                  </div>
                  <div className="mt-2"><ProgressBar pct={c.totals.pct} color={c.at_risk ? "#f43f5e" : "#6366f1"} /></div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {SERVICE_KEYS.filter((k) => c.by_service[k] && (c.by_service[k].target > 0 || c.by_service[k].total > 0)).map((k) => (
                      <span key={k} {...chipProps(k, "rounded-md px-1.5 py-0.5 text-[11px] font-semibold")}>{serviceMeta(k).short} {c.by_service[k].completed}/{c.by_service[k].target}</span>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── Action panels ── */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
            <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900"><CalendarClock className="h-4 w-4 text-indigo-500" /> {upcomingTitle}</h3>
            <span className="text-xs text-slate-400">{viewing === "past" ? `${dueThisWeek.length} open` : `${fmtDay(winStart)} – ${fmtDay(weekEnd)}`}</span>
          </div>
          <TaskList tasks={dueThisWeek} loading={tasks === null} clientName={clientName} memberName={memberName} openTask={openTask} empty={viewing === "past" ? "Everything from this month is finished." : "Nothing due in this period."} today={today} />
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
            <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900"><AlertTriangle className="h-4 w-4 text-rose-500" /> Needs attention</h3>
            <span className="text-xs text-slate-400">{overdueTasks.length} overdue · {atRisk.length} client{atRisk.length === 1 ? "" : "s"} at risk</span>
          </div>
          {atRisk.length > 0 && (
            <div className="flex flex-wrap gap-1.5 border-b border-slate-50 px-5 py-3">
              {atRisk.slice(0, 8).map((c) => (
                <button key={c.client_id} onClick={() => setPanel({ client: c, service: "" })}
                  className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700 ring-1 ring-inset ring-rose-200 hover:bg-rose-100">
                  {c.name}<span className="text-rose-500">{c.totals.overdue ? `${c.totals.overdue} overdue` : `${c.totals.pct}%`}</span>
                </button>
              ))}
            </div>
          )}
          <TaskList tasks={overdueTasks} loading={tasks === null} clientName={clientName} memberName={memberName} openTask={openTask} empty={atRisk.length ? "No overdue tasks." : "All clear — nothing overdue and no client behind."} today={today} />
        </div>
      </div>

      {team && (team.members || []).some((m) => m.counts.total > 0) && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
            <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900"><Users2 className="h-4 w-4 text-indigo-500" /> Team workload</h3>
            <Link to="/work-hub/team-workload" state={{ month }} className="text-xs font-medium text-indigo-600 hover:underline">View all</Link>
          </div>
          <div className="grid gap-x-8 gap-y-3 px-5 py-4 sm:grid-cols-2 xl:grid-cols-3">
            {team.members.filter((m) => m.counts.total > 0).slice(0, 6).map((m) => {
              const open = m.counts.total - m.counts.completed - m.counts.cancelled;
              return (
                <div key={m.user_id} className="flex items-center gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-[11px] font-bold text-indigo-700">{initials(m.name)}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="truncate font-medium text-slate-800">{m.name}</span>
                      <span className="shrink-0 text-xs text-slate-500">{m.counts.completed}/{m.counts.total} done{m.counts.overdue ? <span className="text-rose-600"> · {m.counts.overdue} late</span> : ""}</span>
                    </div>
                    <div className="mt-1"><ProgressBar pct={m.pct} color={m.counts.overdue ? "#f43f5e" : "#6366f1"} /></div>
                    <div className="mt-0.5 text-[11px] text-slate-400">{open} open</div>
                  </div>
                </div>
              );
            })}
          </div>
          {team.unassigned && team.unassigned.total > 0 && <div className="border-t border-slate-50 px-5 py-2.5 text-xs text-amber-700">{team.unassigned.total} task(s) this month aren't assigned to anyone.</div>}
        </div>
      )}

      {panel && (
        <ClientPanel
          client={panel.client} initialService={panel.service} month={month} businessId={businessId}
          members={members} canManage={canManage} onClose={() => setPanel(null)}
          openTask={openTask} newTask={newTask} refresh={refresh} clients={clients}
        />
      )}
    </div>
  );
}

function TaskList({ tasks, loading, clientName, memberName, openTask, empty, today }) {
  const [all, setAll] = useState(false);
  if (loading) return <div className="px-5 py-8 text-center text-sm text-slate-400">Loading…</div>;
  if (!tasks.length) return <div className="px-5 py-8 text-center text-sm text-slate-400">{empty}</div>;
  const shown = all ? tasks : tasks.slice(0, 6);
  return (
    <div>
      <ul className="divide-y divide-slate-50">
        {shown.map((x) => {
          const m = serviceMeta(x.service); const over = isOverdue(x, today);
          const who = (x.assigned_users || []).map((u) => memberName[u]).filter(Boolean)[0];
          return (
            <li key={x._id}>
              <button onClick={() => openTask(x)} className="flex w-full items-center gap-3 px-5 py-2.5 text-left hover:bg-slate-50">
                <span className="h-8 w-1 shrink-0 rounded-full" style={{ backgroundColor: m.color }} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-900">{x.title}</span>
                  <span className="block truncate text-xs text-slate-500">{clientName[x.client_id] || "Client"} · {m.label}{who ? ` · ${who}` : " · Unassigned"}</span>
                </span>
                <span className={`shrink-0 text-xs font-medium ${over ? "text-rose-600" : "text-slate-500"}`}>{String(x.due_date).slice(0, 10) === today ? "Today" : fmtDay(x.due_date)}</span>
              </button>
            </li>
          );
        })}
      </ul>
      {tasks.length > 6 && (
        <button onClick={() => setAll((v) => !v)} className="w-full border-t border-slate-50 px-5 py-2.5 text-xs font-medium text-indigo-600 hover:bg-slate-50">
          {all ? "Show less" : `Show all ${tasks.length}`}
        </button>
      )}
    </div>
  );
}
